import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { DashboardTab } from "@/config/dashboardWidgets";
import type { DashboardPeriod } from "@/utils/dashboardDateUtils";

export interface DashboardPeriodSelection {
  period: DashboardPeriod;
  offset: number;
}

interface DashboardPeriodState {
  selections: Record<string, DashboardPeriodSelection>;
  activeTabs: Record<string, DashboardTab>;
  readingContexts: Record<string, { widgetId: string | null; scrollY: number; viewportOffset: number }>;
  getSelection: (scope: string, tab: DashboardTab) => DashboardPeriodSelection;
  setSelection: (scope: string, tab: DashboardTab, selection: DashboardPeriodSelection) => void;
  getActiveTab: (scope: string) => DashboardTab;
  setActiveTab: (scope: string, tab: DashboardTab) => void;
  setReadingContext: (scope: string, tab: DashboardTab, context: { widgetId: string | null; scrollY: number; viewportOffset: number }) => void;
}

const DEFAULT_SELECTION: DashboardPeriodSelection = { period: "month", offset: 0 };

const keyFor = (scope: string, tab: DashboardTab) => `${scope}:${tab}`;

export const useDashboardPeriodStore = create<DashboardPeriodState>()(
  persist(
    (set, get) => ({
      selections: {},
      activeTabs: {},
      readingContexts: {},
      getSelection: (scope, tab) => get().selections[keyFor(scope, tab)] ?? DEFAULT_SELECTION,
      setSelection: (scope, tab, selection) => set((state) => ({
        selections: { ...state.selections, [keyFor(scope, tab)]: selection },
      })),
      getActiveTab: (scope) => get().activeTabs[scope] ?? "overview",
      setActiveTab: (scope, tab) => set((state) => ({
        activeTabs: { ...state.activeTabs, [scope]: tab },
      })),
      setReadingContext: (scope, tab, context) => set((state) => ({
        readingContexts: { ...state.readingContexts, [keyFor(scope, tab)]: context },
      })),
    }),
    {
      name: "admin-dashboard-period-v2",
      partialize: (state) => ({ selections: state.selections, activeTabs: state.activeTabs, readingContexts: state.readingContexts }),
    },
  ),
);